import { FC, ReactNode, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { IconMailWoosh } from '../../icons/Icons';
import language from '../../util/language';
import ForgotPasswordContext, {
    ForgotPasswordProvider,
    EForgotPasswordUiState,
} from '../../contexts/ForgotPasswordContext';
import styles from './ForgotPasswordScreen.module.scss';
import common from '../../util/scss/common.module.scss';
import API from '../../api/api';
import { Alert, AlertTitle, Box, Icon, Link, Stack, TextField, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ScreenHeader from '../../components/ScreenHeader/ScreenHeader';
import { ELDFlags, ELinks, ERoutes } from '../../util/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import VerifyWithPasscode from '../../components/VerifyWithPasscode/VerifyWithPasscode';

const ForgotPasswordForm: FC = () => {
    const {
        forgotPasswordUiState,
        email,
        setEmail,
        emailIsValid,
        submitForgotPasswordForm,
        emailValidationMessage,
        errorMessage,
        errorTitle,
    } = useContext(ForgotPasswordContext);
    const [isLoading, setIsLoading] = useState(false);
    const { [ELDFlags.useConsumerPasscodeVerification]: usePasscodeForVerification } = useFlags();
    const theme = useTheme();

    const handleFormSubmit = async (event: SyntheticEvent) => {
        event.preventDefault();
        API.post.trackEvent({ event: 'Sign In || Consumer Forgot Password | Button - Send link' });
        setIsLoading(true);
        await submitForgotPasswordForm();
        setIsLoading(false);
    };

    const defaultHeader: ReactNode = (
        <ScreenHeader
            title={language.forgotPassword.header}
            desc={
                usePasscodeForVerification
                    ? language.forgotPassword.subheaderPasscode
                    : language.forgotPassword.subheader
            }
        />
    );
    const linkExpiredHeader: ReactNode = (
        <>
            <ScreenHeader
                title={language.forgotPassword.linkExpired.header}
                desc={language.forgotPassword.linkExpired.subheader}
            />
        </>
    );

    useEffect(() => {
        API.post.trackEvent({ event: 'Sign In || Consumer Forgot Password' });
    }, []);

    return (
        <>
            {forgotPasswordUiState === EForgotPasswordUiState.expired ? linkExpiredHeader : defaultHeader}
            {forgotPasswordUiState === EForgotPasswordUiState.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorTitle && <AlertTitle sx={{ typography: 'subtitle2' }}>{errorTitle}</AlertTitle>}
                    {errorMessage}
                </Alert>
            )}

            <Stack component="form" spacing={2}>
                <TextField
                    label={language.common.email}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    error={!emailIsValid}
                    helperText={!emailIsValid && emailValidationMessage}
                    size="small"
                    autoFocus
                />

                <LoadingButton
                    variant="contained"
                    onClick={handleFormSubmit}
                    disabled={isLoading}
                    loading={isLoading}
                    className={common.buttonShim}
                    size="large"
                    type="submit"
                >
                    {usePasscodeForVerification
                        ? language.forgotPassword.sendPasscodeCTA
                        : forgotPasswordUiState === EForgotPasswordUiState.expired
                        ? language.forgotPassword.linkExpired.submitEmailCTA
                        : language.forgotPassword.submitEmailCTA}
                </LoadingButton>
                <Box>
                    <Link
                        href={ELinks.cantAccessEmailArticle}
                        target="_blank"
                        variant="subtitle2"
                        color={theme.palette.text.primary}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box>{language.forgotPassword.cantAccessEmailCta}</Box>
                            <Icon>open_in_new</Icon>
                        </Stack>
                    </Link>
                </Box>
            </Stack>
        </>
    );
};

const ForgotPasswordSuccess: FC = () => {
    const { email, submitVerifyPasscode, resendPasscode, resetPasswordRouterState } = useContext(ForgotPasswordContext);
    const { [ELDFlags.useConsumerPasscodeVerification]: usePasscodeForVerification } = useFlags();
    return (
        <>
            {usePasscodeForVerification ? (
                <VerifyWithPasscode
                    email={email}
                    onSuccessRedirect={{
                        pathname: ERoutes.resetPassword,
                        state: resetPasswordRouterState,
                    }}
                    onResendPasscode={resendPasscode}
                    onVerifyPasscode={submitVerifyPasscode}
                />
            ) : (
                <Box>
                    <section className={common.icon}>
                        <IconMailWoosh />
                    </section>
                    <header>
                        <h1 className={common.pageHeader}>{language.forgotPassword.success.header}</h1>
                    </header>
                    <main className={styles.notificationContent}>
                        <p className={styles.emailAddress}>{email}</p>
                        <p>{language.forgotPassword.success.desc}</p>
                    </main>
                </Box>
            )}
        </>
    );
};

const ForgotPasswordUi: FC = () => {
    const { forgotPasswordUiState } = useContext(ForgotPasswordContext);
    switch (forgotPasswordUiState) {
        case EForgotPasswordUiState.default:
        case EForgotPasswordUiState.expired:
        case EForgotPasswordUiState.error:
            return <ForgotPasswordForm />;
        case EForgotPasswordUiState.success:
            return <ForgotPasswordSuccess />;
        default:
            console.error('Invalid Forgot Password UI state');
            return <Alert severity="error">{language.validation.genericError}</Alert>;
    }
};

const ForgotPasswordScreen: FC = () => {
    return (
        <ForgotPasswordProvider>
            <PageLayoutV2>
                <ForgotPasswordUi />
            </PageLayoutV2>
        </ForgotPasswordProvider>
    );
};

export default ForgotPasswordScreen;
